/**
 * useContentMermaid - Mermaid 图表错误检查和修复逻辑
 * 从 ContentEdit.tsx 提取
 */
import { useState, useCallback } from 'react';
import mermaid from 'mermaid';
import { OutlineItem } from '../types';
import { contentApi, getErrorMessage } from '../services/api';
import { preprocessMermaidCode, getGanttAfterMismatches, escapeRegex } from '../utils/mermaidPreprocessor';

export interface MermaidError {
  line: number;
  message: string;
  snippet: string;
  context?: string;
  expected?: string;
  got?: string;
}

export interface ChapterMermaidErrors {
  item: OutlineItem;
  errors: MermaidError[];
}

interface UseContentMermaidProps {
  effectiveLeafItems: OutlineItem[];
  projectId?: number;
  draftStore: { upsertChapterContent: (id: string, content: string) => void };
  generationMode: string;
  activeSection: 'tech' | 'biz';
  setBizLeafItems: React.Dispatch<React.SetStateAction<OutlineItem[]>>;
  setLeafItems: React.Dispatch<React.SetStateAction<OutlineItem[]>>;
  setMessage: React.Dispatch<React.SetStateAction<{ type: 'success' | 'error'; text: string } | null>>;
}

const MAX_MERMAID_FIX_RETRIES = 2;

export function useContentMermaid({
  effectiveLeafItems,
  projectId,
  draftStore,
  generationMode,
  activeSection,
  setBizLeafItems,
  setLeafItems,
  setMessage,
}: UseContentMermaidProps) {
  const [mermaidErrors, setMermaidErrors] = useState<ChapterMermaidErrors[] | null>(null);
  const [showMermaidErrors, setShowMermaidErrors] = useState(false);
  const [checkingMermaid, setCheckingMermaid] = useState(false);
  const [fixingMermaidIds, setFixingMermaidIds] = useState<Set<string>>(new Set());
  const [fixedMermaidIds, setFixedMermaidIds] = useState<Set<string>>(new Set());
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());
  const [fixingAllMermaid, setFixingAllMermaid] = useState(false);
  const [fixMermaidProgress, setFixMermaidProgress] = useState({ current: 0, total: 0 });

  /**
   * 验证一段 Markdown 内容中的 mermaid 代码块是否有语法错误
   */
  const validateMermaidInContent = useCallback(async (content: string): Promise<MermaidError[]> => {
    const errors: MermaidError[] = [];
    const mermaidRegex = /```mermaid\s*\n([\s\S]*?)```/g;
    let match;
    while ((match = mermaidRegex.exec(content)) !== null) {
      const rawCode = match[1].split('\n').map(line => line.trimEnd()).join('\n').trim();
      if (!rawCode) continue;

      const ganttMismatches = getGanttAfterMismatches(rawCode);
      for (const gm of ganttMismatches) {
        const beforeMatch = content.substring(0, match.index);
        const baseLine = beforeMatch.split('\n').length;
        const rawLines = rawCode.split('\n');
        let ganttLine = 1;
        const afterPattern = new RegExp(`\\bafter\\s+${escapeRegex(gm.originalRef)}\\b`, 'i');
        for (let i = 0; i < rawLines.length; i++) {
          if (afterPattern.test(rawLines[i])) {
            ganttLine = i + 1;
            break;
          }
        }
        const actualLine = baseLine + ganttLine;
        const snippet = rawLines[ganttLine - 1] || '';
        if (gm.resolvedRef) {
          errors.push({
            line: actualLine,
            message: `Gantt after "${gm.originalRef}" 不是任务名，已自动修正为 "${gm.resolvedRef}"`,
            snippet,
          });
        } else {
          errors.push({
            line: actualLine,
            message: `Gantt after "${gm.originalRef}" 引用了不存在的任务`,
            snippet,
          });
        }
      }

      let code = preprocessMermaidCode(rawCode);
      if (!code) continue;
      const beforeMatch = content.substring(0, match.index);
      const baseLine = beforeMatch.split('\n').length;
      try {
        await mermaid.parse(code);
      } catch (err: any) {
        const rawMessage: string = err.message || String(err);
        let message: string;
        const expectMatch = rawMessage.match(/Expecting\s+'([^']+)'/i);
        const gotMatch = rawMessage.match(/got\s+'([^']+)'/i);
        if (expectMatch && gotMatch) {
          message = `期望 ${expectMatch[1]}，但收到 ${gotMatch[1]}`;
        } else {
          const lineErrMatch = rawMessage.match(/Parse error on line \d+:\s*(.+)/i);
          if (lineErrMatch) {
            message = lineErrMatch[1].trim();
          } else {
            const firstLine = rawMessage.split('\n')[0].replace(/^Error:\s*/i, '').trim();
            message = firstLine || rawMessage.substring(0, 120);
          }
        }
        if (message.length > 120) message = message.substring(0, 120) + '...';

        let context: string | undefined;
        const contextMatch = rawMessage.match(/\.\.\.(.+?)\n(-+)\^/);
        if (contextMatch) {
          context = `...${contextMatch[1]}\n${contextMatch[2]}^`;
        }

        let expected: string | undefined;
        let got: string | undefined;
        if (expectMatch) expected = expectMatch[1];
        if (gotMatch) got = gotMatch[1];

        const mermaidLineMatch = rawMessage.match(/Parse error on line (\d+)/i);
        const mermaidInternalLine = mermaidLineMatch ? parseInt(mermaidLineMatch[1]) : 1;
        const actualLine = baseLine + mermaidInternalLine;
        const codeLines = code.split('\n');
        const errorLineCode = codeLines[mermaidInternalLine - 1] || code.substring(0, 60);

        errors.push({ line: actualLine, message, snippet: errorLineCode, context, expected, got });
      }
    }
    return errors;
  }, []);

  const checkMermaidErrors = useCallback(async () => {
    setCheckingMermaid(true);
    const results: ChapterMermaidErrors[] = [];
    try {
      for (const item of effectiveLeafItems) {
        if (!item.content) continue;
        const mermaidRegex = /```mermaid\s*\n([\s\S]*?)```/g;
        let match;
        while ((match = mermaidRegex.exec(item.content)) !== null) {
          const rawCode = match[1].split('\n').map(line => line.trimEnd()).join('\n').trim();
          if (!rawCode) continue;

          const ganttMismatches = getGanttAfterMismatches(rawCode);
          if (ganttMismatches.length > 0) {
            const beforeMatch = item.content.substring(0, match.index);
            const baseLine = beforeMatch.split('\n').length;
            const rawLines = rawCode.split('\n');
            const gmErrors: MermaidError[] = [];
            for (const gm of ganttMismatches) {
              let ganttLine = 1;
              const afterPattern = new RegExp(`\\bafter\\s+${escapeRegex(gm.originalRef)}\\b`, 'i');
              for (let i = 0; i < rawLines.length; i++) {
                if (afterPattern.test(rawLines[i])) {
                  ganttLine = i + 1;
                  break;
                }
              }
              const actualLine = baseLine + ganttLine;
              const snippet = rawLines[ganttLine - 1] || '';
              if (gm.resolvedRef) {
                gmErrors.push({
                  line: actualLine,
                  message: `Gantt after "${gm.originalRef}" 不是任务名，已自动修正为 "${gm.resolvedRef}"`,
                  snippet,
                });
              } else {
                gmErrors.push({
                  line: actualLine,
                  message: `Gantt after "${gm.originalRef}" 引用了不存在的任务`,
                  snippet,
                });
              }
            }
            const existing = results.find(r => r.item.id === item.id);
            if (existing) {
              existing.errors.push(...gmErrors);
            } else {
              results.push({ item, errors: gmErrors });
            }
          }

          let code = preprocessMermaidCode(rawCode);
          if (!code) continue;
          const beforeMatch = item.content.substring(0, match.index);
          const baseLine = beforeMatch.split('\n').length;
          try {
            await mermaid.parse(code);
          } catch (err: any) {
            const rawMessage: string = err.message || String(err);
            let message: string;
            const expectMatch = rawMessage.match(/Expecting\s+'([^']+)'/i);
            const gotMatch = rawMessage.match(/got\s+'([^']+)'/i);
            if (expectMatch && gotMatch) {
              message = `期望 ${expectMatch[1]}，但收到 ${gotMatch[1]}`;
            } else {
              const lineErrMatch = rawMessage.match(/Parse error on line \d+:\s*(.+)/i);
              if (lineErrMatch) {
                message = lineErrMatch[1].trim();
              } else {
                const firstLine = rawMessage.split('\n')[0].replace(/^Error:\s*/i, '').trim();
                message = firstLine || rawMessage.substring(0, 120);
              }
            }
            if (message.length > 120) message = message.substring(0, 120) + '...';

            let context: string | undefined;
            const contextMatch = rawMessage.match(/\.\.\.(.+?)\n(-+)\^/);
            if (contextMatch) {
              context = `...${contextMatch[1]}\n${contextMatch[2]}^`;
            }

            let expected: string | undefined;
            let got: string | undefined;
            if (expectMatch) expected = expectMatch[1];
            if (gotMatch) got = gotMatch[1];

            const mermaidLineMatch = rawMessage.match(/Parse error on line (\d+)/i);
            const mermaidInternalLine = mermaidLineMatch ? parseInt(mermaidLineMatch[1]) : 1;
            const actualLine = baseLine + mermaidInternalLine;
            const codeLines = code.split('\n');
            const errorLineCode = codeLines[mermaidInternalLine - 1] || code.substring(0, 60);

            const errors = [{ line: actualLine, message, snippet: errorLineCode, context, expected, got }];
            const existing = results.find(r => r.item.id === item.id);
            if (existing) {
              existing.errors.push(...errors);
            } else {
              results.push({ item, errors });
            }
          }
        }
      }
    } finally {
      setMermaidErrors(results.length > 0 ? results : []);
      setShowMermaidErrors(true);
      setCheckingMermaid(false);
    }
  }, [effectiveLeafItems]);

  /** 核心修复逻辑：修复单个章节，含验证 + 重试 */
  const fixSingleChapter = useCallback(async (chapter: ChapterMermaidErrors): Promise<boolean> => {
    const item = chapter.item;
    const content = item.content;
    if (!content || !projectId) return false;

    let currentContent = content;
    let currentErrors = chapter.errors.map(e => e.message);

    for (let attempt = 0; attempt <= MAX_MERMAID_FIX_RETRIES; attempt++) {
      try {
        const res = await contentApi.fixMermaid({ content: currentContent, errors: currentErrors });
        if (!res.data?.success || !res.data.content) {
          if (attempt >= MAX_MERMAID_FIX_RETRIES) return false;
          continue;
        }
        const fixedContent = res.data.content;
        const remainingErrors = await validateMermaidInContent(fixedContent);
        if (remainingErrors.length === 0) {
          const setItemsFunc = generationMode === 'full' && activeSection === 'biz' ? setBizLeafItems : setLeafItems;
          setItemsFunc(prev => prev.map(li =>
            li.id === item.id ? { ...li, content: fixedContent } : li
          ));
          draftStore.upsertChapterContent(item.id, fixedContent);
          const section = generationMode === 'full' ? activeSection : 'tech';
          const { projectApi } = await import('../services/api');
          projectApi.saveChapterContent(projectId, item.id, fixedContent, section).catch(() => {});
          setFixedMermaidIds(prev => new Set(prev).add(item.id));
          setMermaidErrors(prev => (prev || []).filter(e => e.item.id !== item.id));
          return true;
        }
        if (attempt < MAX_MERMAID_FIX_RETRIES) {
          currentContent = fixedContent;
          currentErrors = remainingErrors.map(e => e.message);
        }
      } catch {
        if (attempt >= MAX_MERMAID_FIX_RETRIES) return false;
      }
    }
    return false;
  }, [projectId, draftStore, validateMermaidInContent, generationMode, activeSection, setBizLeafItems, setLeafItems]);

  // 一键修复某个章节的 mermaid 错误
  const fixMermaidChapter = useCallback(async (chapter: ChapterMermaidErrors) => {
    const item = chapter.item;
    if (!item.content || !projectId) return;

    setFixingMermaidIds(prev => new Set(prev).add(item.id));
    try {
      const ok = await fixSingleChapter(chapter);
      if (!ok) {
        setMessage({ type: 'error', text: `修复 ${item.title} 的 mermaid 错误失败（已重试 ${MAX_MERMAID_FIX_RETRIES} 次），请手动编辑` });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: `修复失败: ${getErrorMessage(err, '请稍后重试')}` });
    } finally {
      setFixingMermaidIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }, [projectId, fixSingleChapter, setMessage]);

  // 全部修复所有 mermaid 错误
  const fixAllMermaid = useCallback(async () => {
    if (!mermaidErrors || mermaidErrors.length === 0) return;
    setFixingAllMermaid(true);
    setFixingMermaidIds(new Set(mermaidErrors.map(e => e.item.id)));
    setFixMermaidProgress({ current: 0, total: mermaidErrors.length });
    let successCount = 0;
    let failCount = 0;
    try {
      for (let i = 0; i < mermaidErrors.length; i++) {
        const chapter = mermaidErrors[i];
        const ok = await fixSingleChapter(chapter);
        if (ok) {
          successCount++;
        } else {
          failCount++;
        }
        setFixMermaidProgress({ current: i + 1, total: mermaidErrors.length });
      }
    } finally {
      setFixingAllMermaid(false);
      setFixingMermaidIds(new Set());
      if (successCount > 0) {
        setMessage({ type: 'success', text: `已成功修复 ${successCount} 个章节的 mermaid 错误${failCount > 0 ? `，${failCount} 个修复失败` : ''}` });
      } else if (failCount > 0) {
        setMessage({ type: 'error', text: `全部 ${failCount} 个章节修复失败，请手动编辑` });
      }
    }
  }, [mermaidErrors, fixSingleChapter, setMessage]);

  return {
    // State
    mermaidErrors,
    showMermaidErrors,
    setShowMermaidErrors,
    checkingMermaid,
    fixingMermaidIds,
    fixedMermaidIds,
    expandedErrors,
    setExpandedErrors,
    fixingAllMermaid,
    fixMermaidProgress,
    // Actions
    validateMermaidInContent,
    checkMermaidErrors,
    fixMermaidChapter,
    fixAllMermaid,
  };
}
